import { MouseAPI, KeyboardAPI, TouchAPI, TouchPoint } from '../types/index.js';

/**
 * Manages user interaction state and provides APIs for mouse, keyboard, and touch input.
 * Handles event processing, state tracking, and gesture recognition for Phase 7.
 */
export class InteractionManager {
  // Mouse state
  private mouseState: MouseAPI = {
    x: 0,
    y: 0,
    isInCanvas: false,
    isPressed: false,
    leftButton: false,
    rightButton: false,
    middleButton: false,
    velocity: { x: 0, y: 0 },
    deltaX: 0,
    deltaY: 0,
    wheelDelta: 0,
    wheelX: 0,
    wheelY: 0,
    wasPressed: false,
    wasReleased: false,
    wasMoved: false
  };

  // Mouse velocity tracking
  private mouseVelocityHistory: Array<{ x: number; y: number; time: number }> = [];

  // Keyboard state
  private keyboardState: KeyboardAPI = {
    isPressed: (key: string) => this.activeKeys.has(key.toLowerCase()),
    wasPressed: (key: string) => this.pressedThisFrame.has(key.toLowerCase()),
    wasReleased: (key: string) => this.releasedThisFrame.has(key.toLowerCase()),
    activeKeys: new Set<string>(),
    pressedThisFrame: new Set<string>(),
    releasedThisFrame: new Set<string>(),
    lastKeyPressed: '',
    lastKeyReleased: '',
    shift: false,
    ctrl: false,
    alt: false,
    meta: false
  };

  private activeKeys = new Set<string>();
  private pressedThisFrame = new Set<string>();
  private releasedThisFrame = new Set<string>();

  // Touch state
  private touchState: TouchAPI = {
    points: [],
    count: 0,
    started: [],
    moved: [],
    ended: [],
    primary: null,
    gestures: {
      isPinching: false,
      isRotating: false,
      isPanning: false,
      isTapping: false,
      pinchScale: 1,
      pinchDelta: 0,
      rotationAngle: 0,
      rotationDelta: 0,
      panDelta: { x: 0, y: 0 },
      tapCount: 0,
      lastTapTime: 0,
      tapPosition: null
    }
  };

  private activeTouches = new Map<number, TouchPoint>();
  private gestureState = {
    initialDistance: 0,
    initialAngle: 0,
    lastPinchScale: 1,
    lastRotationAngle: 0,
    panStartPosition: { x: 0, y: 0 },
    tapStartTime: 0,
    tapCount: 0,
    lastTapTime: 0
  };

  constructor() {}

  /**
   * Processes mouse update from the host
   */
  updateMouse(data: {
    x: number;
    y: number;
    buttons: number;
    deltaX: number;
    deltaY: number;
    wheelDeltaX: number;
    wheelDeltaY: number;
    isInCanvas?: boolean;
    timestamp: number;
  }): void {
    // Convert coordinates to canvas space
    const canvasX = data.x;
    const canvasY = data.y;

    // Calculate deltas
    const deltaX = canvasX - this.mouseState.x;
    const deltaY = canvasY - this.mouseState.y;

    // Update velocity with smoothing
    this.updateMouseVelocity(deltaX, deltaY, data.timestamp);

    // Detect button state changes
    const prevPressed = this.mouseState.isPressed;
    const currentPressed = data.buttons > 0;

    this.mouseState.wasPressed = !prevPressed && currentPressed;
    this.mouseState.wasReleased = prevPressed && !currentPressed;
    this.mouseState.wasMoved = deltaX !== 0 || deltaY !== 0;

    // Update mouse state
    this.mouseState.x = canvasX;
    this.mouseState.y = canvasY;
    this.mouseState.deltaX = deltaX;
    this.mouseState.deltaY = deltaY;
    this.mouseState.isPressed = currentPressed;
    this.mouseState.leftButton = (data.buttons & 1) !== 0;
    this.mouseState.rightButton = (data.buttons & 2) !== 0;
    this.mouseState.middleButton = (data.buttons & 4) !== 0;
    this.mouseState.isInCanvas = data.isInCanvas !== undefined ? data.isInCanvas : true;

    // Update wheel
    this.mouseState.wheelDelta = data.wheelDeltaY;
    this.mouseState.wheelX = data.wheelDeltaX;
    this.mouseState.wheelY = data.wheelDeltaY;
  }

  /**
   * Updates mouse velocity with smoothing
   */
  private updateMouseVelocity(deltaX: number, deltaY: number, timestamp: number): void {
    // Add current velocity sample
    this.mouseVelocityHistory.push({ x: deltaX, y: deltaY, time: timestamp });

    // Keep only recent samples (last 100ms)
    const cutoff = timestamp - 100;
    this.mouseVelocityHistory = this.mouseVelocityHistory.filter(sample => sample.time > cutoff);

    // Calculate smoothed velocity
    if (this.mouseVelocityHistory.length > 1) {
      const recent = this.mouseVelocityHistory.slice(-5); // Last 5 samples
      const avgX = recent.reduce((sum, s) => sum + s.x, 0) / recent.length;
      const avgY = recent.reduce((sum, s) => sum + s.y, 0) / recent.length;
      
      this.mouseState.velocity.x = avgX;
      this.mouseState.velocity.y = avgY;
    } else {
      this.mouseState.velocity.x = deltaX;
      this.mouseState.velocity.y = deltaY;
    }
  }

  /**
   * Processes keyboard update from the host
   */
  updateKeyboard(data: {
    type: 'keydown' | 'keyup';
    key: string;
    code: string;
    shiftKey: boolean;
    ctrlKey: boolean;
    altKey: boolean;
    metaKey: boolean;
    timestamp: number;
  }): void {
    const key = data.key.toLowerCase();

    if (data.type === 'keydown') {
      if (!this.activeKeys.has(key)) {
        this.activeKeys.add(key);
        this.pressedThisFrame.add(key);
        this.keyboardState.lastKeyPressed = data.key;
      }
    } else if (data.type === 'keyup') {
      this.activeKeys.delete(key);
      this.releasedThisFrame.add(key);
      this.keyboardState.lastKeyReleased = data.key;
    }

    // Update modifier keys
    this.keyboardState.shift = data.shiftKey;
    this.keyboardState.ctrl = data.ctrlKey;
    this.keyboardState.alt = data.altKey;
    this.keyboardState.meta = data.metaKey;
  }

  /**
   * Processes touch update from the host
   */
  updateTouch(data: {
    type: 'touchstart' | 'touchmove' | 'touchend' | 'touchcancel';
    touches: Array<{
      identifier: number;
      clientX: number;
      clientY: number;
      pressure: number;
      radiusX: number;
      radiusY: number;
      rotationAngle: number;
      force: number;
    }>;
    timestamp: number;
  }): void {
    // Clear frame-based touch events
    this.touchState.started = [];
    this.touchState.moved = [];
    this.touchState.ended = [];

    // Process touches based on event type
    if (data.type === 'touchstart') {
      this.processTouchStart(data.touches, data.timestamp);
    } else if (data.type === 'touchmove') {
      this.processTouchMove(data.touches, data.timestamp);
    } else if (data.type === 'touchend' || data.type === 'touchcancel') {
      this.processTouchEnd(data.touches, data.timestamp);
    }

    // Update touch array and count
    this.touchState.points = Array.from(this.activeTouches.values());
    this.touchState.count = this.touchState.points.length;
    this.touchState.primary = this.touchState.points[0] || null;

    // Update gestures
    this.updateGestures();
  }

  /**
   * Processes touch start events
   */
  private processTouchStart(touches: any[], timestamp: number): void {
    for (const touch of touches) {
      const touchPoint = this.createTouchPoint(touch, timestamp, true);
      this.activeTouches.set(touch.identifier, touchPoint);
      this.touchState.started.push(touchPoint);
    }

    // Handle tap gesture start
    if (this.touchState.count === 1) {
      this.gestureState.tapStartTime = timestamp;
      const touch = this.touchState.points[0];
      this.touchState.gestures.tapPosition = { x: touch.x, y: touch.y };
    }
  }

  /**
   * Processes touch move events
   */
  private processTouchMove(touches: any[], timestamp: number): void {
    for (const touch of touches) {
      const existing = this.activeTouches.get(touch.identifier);
      if (existing) {
        const updated = this.createTouchPoint(touch, timestamp, false, existing);
        this.activeTouches.set(touch.identifier, updated);
        this.touchState.moved.push(updated);
      }
    }
  }

  /**
   * Processes touch end events
   */
  private processTouchEnd(touches: any[], timestamp: number): void {
    for (const touch of touches) {
      const existing = this.activeTouches.get(touch.identifier);
      if (existing) {
        const ended = { ...existing, isEnding: true, isActive: false };
        this.touchState.ended.push(ended);
        this.activeTouches.delete(touch.identifier);
      }
    }

    // Handle tap gesture completion
    if (this.touchState.count === 0 && this.gestureState.tapStartTime > 0) {
      const tapDuration = timestamp - this.gestureState.tapStartTime;
      if (tapDuration < 300) { // Quick tap
        this.handleTap(timestamp);
      }
      this.gestureState.tapStartTime = 0;
    }
  }

  /**
   * Creates a touch point from raw touch data
   */
  private createTouchPoint(
    touch: any, 
    timestamp: number, 
    isNew: boolean, 
    previous?: TouchPoint
  ): TouchPoint {
    const x = touch.clientX;
    const y = touch.clientY;
    
    const deltaX = previous ? x - previous.x : 0;
    const deltaY = previous ? y - previous.y : 0;
    
    // Calculate velocity
    const timeDelta = previous ? timestamp - (previous as any).timestamp : 16;
    const velocityX = timeDelta > 0 ? deltaX / timeDelta * 1000 : 0;
    const velocityY = timeDelta > 0 ? deltaY / timeDelta * 1000 : 0;

    return {
      id: touch.identifier,
      x,
      y,
      pressure: touch.pressure || 0,
      radius: Math.max(touch.radiusX || 0, touch.radiusY || 0),
      radiusX: touch.radiusX || 0,
      radiusY: touch.radiusY || 0,
      rotationAngle: touch.rotationAngle || 0,
      force: touch.force || touch.pressure || 0,
      deltaX,
      deltaY,
      velocity: { x: velocityX, y: velocityY },
      isNew,
      isActive: true,
      isEnding: false
    };
  }

  /**
   * Updates gesture recognition
   */
  private updateGestures(): void {
    const touches = this.touchState.points;
    const gestures = this.touchState.gestures;

    if (touches.length === 2) {
      // Two-finger gestures: pinch and rotate
      const touch1 = touches[0];
      const touch2 = touches[1];
      
      const distance = Math.sqrt(
        Math.pow(touch2.x - touch1.x, 2) + Math.pow(touch2.y - touch1.y, 2)
      );
      
      const angle = Math.atan2(touch2.y - touch1.y, touch2.x - touch1.x);

      if (this.gestureState.initialDistance === 0) {
        this.gestureState.initialDistance = distance;
        this.gestureState.initialAngle = angle;
        this.gestureState.lastPinchScale = 1;
        this.gestureState.lastRotationAngle = 0;
      }

      // Pinch gesture
      const scale = distance / this.gestureState.initialDistance;
      const scaleDelta = scale - this.gestureState.lastPinchScale;
      
      gestures.isPinching = Math.abs(scaleDelta) > 0.01;
      gestures.pinchScale = scale;
      gestures.pinchDelta = scaleDelta;
      this.gestureState.lastPinchScale = scale;

      // Rotation gesture
      const rotationAngle = angle - this.gestureState.initialAngle;
      const rotationDelta = rotationAngle - this.gestureState.lastRotationAngle;
      
      gestures.isRotating = Math.abs(rotationDelta) > 0.02;
      gestures.rotationAngle = rotationAngle;
      gestures.rotationDelta = rotationDelta;
      this.gestureState.lastRotationAngle = rotationAngle;

    } else {
      // Reset two-finger gesture state
      this.gestureState.initialDistance = 0;
      gestures.isPinching = false;
      gestures.isRotating = false;
      gestures.pinchDelta = 0;
      gestures.rotationDelta = 0;
    }

    // Pan gesture (one or more fingers)
    if (touches.length > 0) {
      const primaryTouch = touches[0];
      if (this.gestureState.panStartPosition.x === 0) {
        this.gestureState.panStartPosition = { x: primaryTouch.x, y: primaryTouch.y };
      }
      
      const panDeltaX = primaryTouch.x - this.gestureState.panStartPosition.x;
      const panDeltaY = primaryTouch.y - this.gestureState.panStartPosition.y;
      const panDistance = Math.sqrt(panDeltaX * panDeltaX + panDeltaY * panDeltaY);
      
      gestures.isPanning = panDistance > 10;
      gestures.panDelta = { x: panDeltaX, y: panDeltaY };
    } else {
      // Reset pan gesture
      this.gestureState.panStartPosition = { x: 0, y: 0 };
      gestures.isPanning = false;
      gestures.panDelta = { x: 0, y: 0 };
    }
  }

  /**
   * Handles tap gesture detection
   */
  private handleTap(timestamp: number): void {
    const timeSinceLastTap = timestamp - this.gestureState.lastTapTime;
    
    if (timeSinceLastTap < 300) {
      // Multi-tap
      this.gestureState.tapCount++;
    } else {
      // New tap sequence
      this.gestureState.tapCount = 1;
    }
    
    this.touchState.gestures.tapCount = this.gestureState.tapCount;
    this.touchState.gestures.lastTapTime = timestamp;
    this.touchState.gestures.isTapping = true;
    this.gestureState.lastTapTime = timestamp;
  }

  /**
   * Called at the start of each frame to reset frame-based events
   */
  frameStart(): void {
    // Reset mouse frame events
    this.mouseState.wasPressed = false;
    this.mouseState.wasReleased = false;
    this.mouseState.wasMoved = false;
    this.mouseState.wheelDelta = 0;
    this.mouseState.wheelX = 0;
    this.mouseState.wheelY = 0;

    // Reset keyboard frame events
    this.pressedThisFrame.clear();
    this.releasedThisFrame.clear();

    // Reset touch gesture frame events
    this.touchState.gestures.isTapping = false;
    this.touchState.gestures.pinchDelta = 0;
    this.touchState.gestures.rotationDelta = 0;
  }

  /**
   * Get current mouse state (read-only)
   */
  getMouseState(): Readonly<MouseAPI> {
    return this.mouseState;
  }

  /**
   * Get current keyboard state (read-only)
   */
  getKeyboardState(): Readonly<KeyboardAPI> {
    return this.keyboardState;
  }

  /**
   * Get current touch state (read-only)
   */
  getTouchState(): Readonly<TouchAPI> {
    return this.touchState;
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.mouseVelocityHistory.length = 0;
    this.activeTouches.clear();
    this.activeKeys.clear();
    this.pressedThisFrame.clear();
    this.releasedThisFrame.clear();
  }
} 